/**
 * LeadValueProvidersStrip — one customer price + potential providers.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { getBids } from "../bidding/services/leadBiddingService.js";
import {
  buildPotentialProviderRows,
  getLeadValueStripModel,
} from "../services/leadValueProviders.js";

const ProviderRow = ({ row }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap={1.5}
    width="100%"
    py={0.75}
    data-testid="lead-provider-row"
  >
    <Box minWidth={0} flex={1}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {row.name}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.75} mt={0.25} flexWrap="wrap">
        <Chip
          label={row.statusLabel}
          size="small"
          color={row.statusKey === "selected" ? "success" : "default"}
          variant="outlined"
          sx={{ height: 22 }}
        />
      </Box>
    </Box>
    {row.amountLabel ? (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flexShrink: 0, textAlign: "right", whiteSpace: "nowrap" }}
      >
        {row.amountPrefix} {row.amountLabel}
      </Typography>
    ) : null}
  </Box>
);

const LeadValueProvidersStrip = ({
  lead,
  quote = null,
  refreshKey = 0,
  onInviteToBid,
  onAssign,
  onViewAllProviders,
}) => {
  const [bids, setBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(false);
  const [bidsError, setBidsError] = useState(null);

  const leadId = lead?._id || lead?.id;
  const valueModel = useMemo(
    () => getLeadValueStripModel(lead, quote),
    [lead, quote]
  );
  const providers = useMemo(
    () => buildPotentialProviderRows(lead, bids),
    [lead, bids]
  );

  useEffect(() => {
    let cancelled = false;
    if (!leadId) return undefined;

    const load = async () => {
      setLoadingBids(true);
      setBidsError(null);
      try {
        const res = await getBids(leadId);
        if (!cancelled) {
          setBids(res?.data || res?.bids || []);
        }
      } catch (err) {
        if (!cancelled) {
          setBids([]);
          setBidsError(err?.message || "Could not load bids");
        }
      } finally {
        if (!cancelled) setLoadingBids(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [leadId, lead?.bidding?.status, refreshKey]);

  return (
    <Box
      data-testid="lead-value-providers-strip"
      sx={{
        mb: 2,
        p: { xs: 1.75, sm: 2, md: 2.5 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "background.paper",
      }}
    >
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          // Tablet+: fill the right with actions (no empty trailing column)
          sm: "minmax(0, 1fr) minmax(0, 1.35fr) minmax(132px, auto)",
        }}
        gap={{ xs: 2, sm: 2, md: 3 }}
        alignItems="stretch"
      >
        <Box minWidth={0}>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, mb: 0.75, lineHeight: 1.25 }}
          >
            {valueModel.routeLabel}
          </Typography>
          {valueModel.metaLine ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {valueModel.metaLine}
            </Typography>
          ) : null}
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ fontWeight: 600, letterSpacing: 0.2 }}
          >
            Est. customer price
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: "primary.main", lineHeight: 1.2 }}
            data-testid="lead-value-price"
          >
            {valueModel.priceLabel || "—"}
          </Typography>
          {valueModel.confidenceLabel ? (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              {valueModel.confidenceLabel}
            </Typography>
          ) : null}
        </Box>

        <Box minWidth={0}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            mb={0.5}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Potential providers
            </Typography>
            {providers.marketplaceLabel ? (
              <Chip
                label={providers.marketplaceLabel}
                size="small"
                variant="outlined"
                sx={{ height: 22 }}
              />
            ) : null}
          </Box>
          {loadingBids ? (
            <Box display="flex" alignItems="center" gap={1} py={1}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading providers…
              </Typography>
            </Box>
          ) : providers.rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={1}>
              {bidsError || providers.emptyMessage}
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={0}>
              {providers.rows.map((row) => (
                <ProviderRow key={row.id} row={row} />
              ))}
            </Stack>
          )}
          {providers.moreCount > 0 ? (
            <Button
              size="small"
              onClick={onViewAllProviders || onAssign}
              sx={{ mt: 0.5, px: 0 }}
            >
              + {providers.moreCount} more on marketplace
            </Button>
          ) : null}
        </Box>

        <Box
          display="flex"
          flexDirection={{ xs: "row", sm: "column" }}
          gap={1}
          justifyContent={{ xs: "stretch", sm: "center" }}
          alignItems="stretch"
          minWidth={{ sm: 132 }}
          sx={{
            gridColumn: { xs: "1 / -1", sm: "auto" },
            pt: { xs: 0.5, sm: 0 },
            borderTop: { xs: "1px solid", sm: "none" },
            borderColor: { xs: "divider", sm: "transparent" },
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={onInviteToBid}
            fullWidth
            sx={{ flex: { xs: 1, sm: "unset" } }}
          >
            Invite to bid
          </Button>
          <Button
            variant="outlined"
            onClick={onAssign}
            fullWidth
            sx={{ flex: { xs: 1, sm: "unset" } }}
          >
            Assign
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LeadValueProvidersStrip;
